import React, {useEffect, useState} from "react";
import {AppContainer} from "./Containers";
import {Button, ButtonVariant, Card, FormGroup, InputGroup, Intent} from "@blueprintjs/core";
import {useSession} from "./SessionContext";
import {Link, useNavigate, useParams} from "react-router";
import {AppToaster} from "./AppToaster";
import {IconNames} from "@blueprintjs/icons";
import {useLanguage} from "../context/LanguageContext";
// Import flag images
import germanFlag from '../assets/de-flag.svg';
import britishFlag from '../assets/gb-flag.svg';

export const LoggedOutIndex = () => {
    const { t, language, setLanguage } = useLanguage();
    const session = useSession();
    const navigate = useNavigate();
    const [loginField, setLoginField] = useState("");

    useEffect(() => {
        if (session.isAuthenticated)
            navigate("/app");
    }, [navigate, session.isAuthenticated]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (loginField.trim() === "") {
            AppToaster.show({
                intent: Intent.WARNING,
                icon: "warning-sign",
                message: t('pages', 'loggedOut', 'emptyLoginError'),
            });
            return;
        }
        session.login(loginField).then(() => {
            AppToaster.show({
                message: t('pages', 'loggedOut', 'loginSuccess'),
                intent: Intent.SUCCESS,
                icon: IconNames.TICK,
            });
            navigate("/app");
        }).catch((reason) => {
            AppToaster.show({
                message: `${t('pages', 'loggedOut', 'loginError')}: ${reason}`,
                intent: Intent.DANGER,
                icon: IconNames.ERROR,
            })
        })
    }

    return <AppContainer>
        <div style={{ width: 300, margin: 'auto', marginTop: 50 }}>
            {/* Language toggle at the top of the login area */}
            <div style={{ textAlign: 'right', marginBottom: 10 }}>
                <Button
                    variant={ButtonVariant.MINIMAL}
                    icon={
                        <img
                            src={language === 'de' ? germanFlag : britishFlag}
                            alt={t('ui', 'changeLanguage')}
                            style={{
                                width: 20,
                                height: 20,
                                borderRadius: 2,
                                verticalAlign: 'middle'
                            }}
                        />
                    }
                    text=""
                    onClick={() => setLanguage(language === 'de' ? 'en' : 'de')}
                    title={t('ui', 'changeLanguage')}
                    style={{ padding: '5px 10px' }}
                />
            </div>

            <Card style={{ padding: 20 }}>
                <h2>{t('pages', 'loggedOut', 'title')}</h2>
                <form onSubmit={handleSubmit}>
                    <FormGroup
                        label={t('pages', 'loggedOut', 'usernameLabel')}
                        labelFor="user-id-input"
                    >
                        <InputGroup
                            id="user-id-input"
                            placeholder={t('pages', 'loggedOut', 'usernamePlaceholder')}
                            type="login"
                            spellCheck={false}
                            value={loginField}
                            onChange={e => setLoginField(e.target.value)}
                        />
                    </FormGroup>
                    <Button intent={Intent.PRIMARY} type="submit" style={{width: '100%'}}>
                        {t('pages', 'loggedOut', 'loginButton')}
                    </Button>
                </form>
            </Card>
            <Link to="/impressum" style={{display: 'block', textAlign: 'center', marginTop: 20}}>
                {t('navbar', 'imprint')}
            </Link>
        </div>
    </AppContainer>
}

export const RouteLoginHandler = () => {
    const { t } = useLanguage();
    const {userId} = useParams();
    const navigate = useNavigate();
    const {login} = useSession();

    useEffect(() => {
        if (userId) {
            login(userId);
        }
        // Get translated welcome message and replace placeholder
        const welcomeMessage = t('pages', 'loggedIn', 'welcome').replace('{name}', userId || '');
        navigate("/app", {
            state: {
                message: welcomeMessage,
                intent: "success"
            }
        });
    }, [userId, navigate, login, t]);

    return <div>{t('ui', 'loading')}</div>;
};

export const RouteLogoutHandler = () => {
    const { t } = useLanguage();
    const {logout} = useSession();
    const navigate = useNavigate();

    useEffect(() => {
        AppToaster.show({
            message: t('pages', 'loggedOut', 'logoutSuccess'),
            intent: Intent.SUCCESS,
            icon: IconNames.TICK,
        });
        logout();
        navigate("/");
    }, [logout, navigate, t]);

    return <div>{t('ui', 'loading')}</div>;
}